import { useCallback, useEffect, useRef, useState } from 'react';
import { Entity } from '@backstage/catalog-model';
import { useEntity } from '@backstage/plugin-catalog-react';
import { useApi, fetchApiRef, discoveryApiRef } from '@backstage/core-plugin-api';
import { Progress, InfoCard } from '@backstage/core-components';

const HERMES_PROJECT_ID = 55373; // MGMTAPPPRYMANAGEMENT/olympus/hermes

type EntryStatus = 'in-progress' | 'completed' | 'failed' | string;

interface HistoryEntry {
  change_name: string;
  requirement: string;
  status: EntryStatus;
  started_at: string;
  completed_at?: string;
  branch?: string;
  mr_url?: string;
  summary?: string;
  files_changed?: string[];
  verification?: Record<string, string>;
}

interface ComponentHistory {
  component: string;
  solution: string;
  updated_at: string;
  entries: HistoryEntry[];
}

export const isZeusHistoryAvailable = (entity: Entity): boolean =>
  Boolean(entity.metadata.annotations?.['olympus.io/solucion']);

function relativeTime(iso: string | undefined): string {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'ahora mismo';
  if (m < 60) return `hace ${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `hace ${h}h`;
  const d = Math.floor(h / 24);
  return `hace ${d} día${d !== 1 ? 's' : ''}`;
}

const STATUS_LABEL: Record<string, string> = {
  'in-progress': 'en progreso',
  completed: 'completado',
  failed: 'fallido',
};

const STATUS_COLOR: Record<string, { bg: string; fg: string }> = {
  'in-progress': { bg: '#3a2f0d', fg: '#f0b429' },
  completed: { bg: '#0d2e1a', fg: '#3fb950' },
  failed: { bg: '#3d1214', fg: '#f85149' },
};

export const EntityZeusHistoryContent = () => {
  const { entity } = useEntity();
  const fetchApi = useApi(fetchApiRef);
  const discoveryApi = useApi(discoveryApiRef);

  const [history, setHistory] = useState<ComponentHistory | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const componentName = entity.metadata.name;

  const fetchHistory = useCallback(async (): Promise<ComponentHistory | 'not-found' | null> => {
    try {
      const proxyBase = await discoveryApi.getBaseUrl('proxy');
      const path = `components/${componentName}.json`;
      const url = `${proxyBase}/gitlabci/projects/${HERMES_PROJECT_ID}/repository/files/${encodeURIComponent(
        path,
      )}/raw?ref=main`;
      const res = await fetchApi.fetch(url);
      if (res.status === 404) return 'not-found';
      if (!res.ok) {
        return null;
      }
      return (await res.json()) as ComponentHistory;
    } catch {
      return null;
    }
  }, [componentName, discoveryApi, fetchApi]);

  const refresh = useCallback(
    async (isBackground: boolean) => {
      if (!isBackground) setLoading(true);
      const result = await fetchHistory();
      if (result === 'not-found') {
        setHistory(null);
        setError(null);
      } else if (result === null) {
        if (!isBackground) setError('No se pudo cargar el historial de Zeus.');
      } else {
        setHistory(result);
        setError(null);
      }
      if (!isBackground) setLoading(false);
    },
    [fetchHistory],
  );

  useEffect(() => {
    refresh(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [componentName]);

  useEffect(() => {
    if (pollRef.current) clearTimeout(pollRef.current);
    const hasInProgress = history?.entries?.some(e => e.status === 'in-progress');
    if (hasInProgress) {
      pollRef.current = setTimeout(() => refresh(true), 30000);
    }
    return () => {
      if (pollRef.current) clearTimeout(pollRef.current);
    };
  }, [history, refresh]);

  if (loading) {
    return (
      <InfoCard title="Change History">
        <Progress />
      </InfoCard>
    );
  }

  if (error) {
    return (
      <InfoCard title="Change History">
        <p style={{ padding: 16, color: '#666' }}>{error}</p>
      </InfoCard>
    );
  }

  const entries = history?.entries ? [...history.entries].reverse() : [];

  return (
    <InfoCard
      title="Change History"
      action={
        <button
          type="button"
          onClick={() => refresh(false)}
          style={{
            background: 'none',
            border: '1px solid #ccc',
            borderRadius: 6,
            padding: '4px 10px',
            cursor: 'pointer',
            fontSize: 12,
          }}
        >
          ↺ Refrescar
        </button>
      }
    >
      {entries.length === 0 && (
        <p style={{ padding: 16, color: '#666' }}>
          Zeus aún no ha hecho cambios en este componente.
        </p>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: entries.length ? 16 : 0 }}>
        {entries.map(entry => {
          const statusColor = STATUS_COLOR[entry.status] ?? { bg: '#333', fg: '#ccc' };
          const filesChanged = entry.files_changed ?? [];
          const verification = entry.verification ?? {};
          const verificationKeys = Object.keys(verification);

          return (
            <div
              key={entry.change_name}
              style={{
                border: '1px solid #30363d',
                borderRadius: 8,
                padding: 14,
                background: '#0d1117',
                color: '#e6edf3',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    padding: '2px 8px',
                    borderRadius: 20,
                    background: statusColor.bg,
                    color: statusColor.fg,
                    flexShrink: 0,
                    marginTop: 2,
                  }}
                >
                  {STATUS_LABEL[entry.status] ?? entry.status}
                </span>
                <span style={{ fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all' }}>
                  {entry.change_name}
                </span>
              </div>

              <p style={{ fontSize: 13, color: '#8b949e', margin: '8px 0 0' }}>{entry.requirement}</p>

              {entry.summary && (
                <p style={{ fontSize: 13, margin: '10px 0 0', lineHeight: 1.5 }}>{entry.summary}</p>
              )}

              {filesChanged.length > 0 && (
                <div style={{ marginTop: 10 }}>
                  <div style={{ fontSize: 11, color: '#8b949e', textTransform: 'uppercase', letterSpacing: '.05em' }}>
                    Archivos modificados
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', marginTop: 4 }}>
                    {filesChanged.map(f => (
                      <span key={f} style={{ fontFamily: 'monospace', fontSize: 11, color: '#c9d1d9' }}>
                        {f}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {verificationKeys.length > 0 && (
                <div style={{ marginTop: 10, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  {verificationKeys.map(k => (
                    <span key={k} style={{ fontSize: 11, color: '#8b949e' }}>
                      {k}: <span style={{ color: '#c9d1d9' }}>{verification[k]}</span>
                    </span>
                  ))}
                </div>
              )}

              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  marginTop: 12,
                  paddingTop: 10,
                  borderTop: '1px solid #21262d',
                  fontSize: 11,
                  color: '#8b949e',
                }}
              >
                <span>{relativeTime(entry.started_at)}</span>
                {entry.branch && <span style={{ fontFamily: 'monospace' }}>{entry.branch}</span>}
                {entry.mr_url && (
                  <a href={entry.mr_url} target="_blank" rel="noopener noreferrer" style={{ marginLeft: 'auto' }}>
                    MR →
                  </a>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </InfoCard>
  );
};
